// src/components/ProtectedRoute.jsx
import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/* ================================================================== */
/* Constants                                                           */
/* ================================================================== */

/**
 * Pages a pending (unapproved) user can still access.
 * Keep this in sync with any routes that need to work pre-approval.
 */
const PENDING_ALLOWED_PATHS = [
  '/profile',
  '/approval-pending',
  '/logout',
  '/settings',
];

/** Prefixes that are always allowed for pending users. */
const PENDING_ALLOWED_PREFIXES = ['/profile/'];

/* ================================================================== */
/* Loading fallback                                                    */
/* ================================================================== */

const AccessCheckLoader = () => (
  <div
    role="status"
    aria-live="polite"
    className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900"
  >
    <div className="text-center">
      <div
        className="w-12 h-12 border-4 border-orange border-t-transparent rounded-full animate-spin mx-auto"
        aria-hidden="true"
      />
      <p className="mt-4 text-gray-600 dark:text-gray-300 text-sm">
        Checking access…
      </p>
    </div>
  </div>
);

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

/**
 * Determine whether a user object counts as "approved" using every
 * field your app might set, in priority order.
 */
const checkIsApproved = (userData, isApprovedFlag) => {
  if (isApprovedFlag === true) return true;
  if (!userData) return false;
  return (
    userData.approved === true ||
    userData.isApproved === true ||
    userData.registrationStatus === 'approved'
  );
};

/**
 * Determine the current lifecycle status of a user.
 * Returns one of: 'approved' | 'pending' | 'rejected' | 'blocked' | 'unknown'
 */
const getUserStatus = (userData, isApprovedFlag) => {
  if (!userData) return isApprovedFlag === true ? 'approved' : 'unknown';

  if (userData.isBlocked === true) return 'blocked';
  if (userData.registrationStatus === 'rejected') return 'rejected';

  if (checkIsApproved(userData, isApprovedFlag)) return 'approved';

  if (
    userData.registrationStatus === 'pending_approval' ||
    userData.approved === false ||
    userData.isApproved === false
  ) {
    return 'pending';
  }

  return 'unknown';
};

/**
 * Normalize a role entry into a lowercase string.
 * Accepts: 'admin' | 'board' | 'member' | { id: 'admin' } | null/undefined
 */
const normalizeRoleEntry = (entry) => {
  if (!entry) return '';
  if (typeof entry === 'string') return entry.trim().toLowerCase();
  if (typeof entry === 'object' && typeof entry.id === 'string') {
    return entry.id.trim().toLowerCase();
  }
  return '';
};

/**
 * Given a role list and the current user's role flags, determine
 * whether the user has *any* of the required roles.
 *
 * Hierarchy: Admin > Board > Member
 */
const hasRequiredRole = (roles, { isAdmin, isBoard, isMember }) => {
  if (!Array.isArray(roles) || roles.length === 0) return true;

  const normalized = roles
    .map(normalizeRoleEntry)
    .filter(Boolean);

  if (normalized.length === 0) return true;

  const flags = {
    admin: isAdmin === true,
    board: isBoard === true,
    member: isMember === true,
  };

  // Direct match first
  if (normalized.some((r) => flags[r])) return true;

  // Hierarchy fallbacks
  if (flags.admin && (normalized.includes('board') || normalized.includes('member'))) {
    return true;
  }
  if (flags.board && normalized.includes('member')) {
    return true;
  }

  return false;
};

/**
 * Check whether the current path is in the "pending allowed" list.
 */
const isPendingAllowedPath = (pathname) =>
  PENDING_ALLOWED_PATHS.includes(pathname) ||
  PENDING_ALLOWED_PREFIXES.some((p) => pathname.startsWith(p));

/* ================================================================== */
/* ProtectedRoute                                                      */
/* ================================================================== */

const ProtectedRoute = ({
  children,
  roles = [],
  /** When true, only requires auth — skips the approval gate. */
  requireApproval = true,
}) => {
  const {
    user,
    loading,
    isAdmin,
    isBoard,
    isMember,
    isApproved,
    userData,
  } = useAuth();
  const location = useLocation();

  /* ---------------- Memoize status (cheap, but avoids re-computation) ---------------- */
  const status = useMemo(
    () => getUserStatus(userData, isApproved),
    [userData, isApproved],
  );

  const roleFlags = useMemo(
    () => ({ isAdmin, isBoard, isMember }),
    [isAdmin, isBoard, isMember],
  );

  /* ---------------- 1. Auth still loading ---------------- */
  if (loading) {
    return <AccessCheckLoader />;
  }

  /* ---------------- 2. Not authenticated ---------------- */
  if (!user) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  /* ---------------- 3. Blocked user — kick them out with a reason ---------------- */
  if (status === 'blocked') {
    return <Navigate to="/login?error=blocked" replace />;
  }

  /* ---------------- 4. Rejected user — clear session, send to login ---------------- */
  if (status === 'rejected') {
    return <Navigate to="/login?error=rejected" replace />;
  }

  /* ---------------- 5. Pending / unapproved user ---------------- */
  if (requireApproval && status !== 'approved') {
    // If we're already on an allowed page, let them through
    if (isPendingAllowedPath(location.pathname)) {
      return children;
    }
    return <Navigate to="/approval-pending" replace />;
  }

  /* ---------------- 6. Role-based access ---------------- */
  if (!hasRequiredRole(roles, roleFlags)) {
    return <Navigate to="/" replace />;
  }

  /* ---------------- Authorized ---------------- */
  return children;
};

export default ProtectedRoute;